import { useCallback, useEffect } from "react"
import IInvoice from "~/interfaces/IInvoice"
import Orders from "~/api2/orders"
import { IFormData, ILineItemField } from "~/components/Admin/Invoices/EditInvoice"
import { LINE_ITEM_KINDS } from "~/constants/line_item_kinds"
import useAdminForm from "~/hooks/useAdminForm"
import { isArray } from "lodash"
import { ElectricPayload } from "~/interfaces/ElectricPrices"

interface UseApplyElectricReturn {
  handleApplyFees: (invoice: IInvoice) => Promise<void>
}

const useApplyElectric = (): UseApplyElectricReturn => {
  const { setValue, watch, resource } = useAdminForm<IFormData>()
  const items = watch("items")

  useEffect(() => {
    if (isArray(items)) {
      const electricItemIndex = items?.findIndex(
        (item: ILineItemField) =>
          item.kind === LINE_ITEM_KINDS.ELECTRIC && items.length === 1 && item.label === "Electric",
      )

      if (electricItemIndex !== -1) {
        const updatedItems = [...items]
        const updatedUnitAmount = Math.max(resource.current_meter_reading - resource.last_meter_reading, 0)

        updatedItems[electricItemIndex] = {
          ...updatedItems[electricItemIndex],
          quantity: updatedUnitAmount,
          unit_amount: updatedItems[electricItemIndex].unit_amount,
          total_amount: updatedItems[electricItemIndex].unit_amount * updatedUnitAmount,
        }

        setValue("items", updatedItems, { shouldValidate: true })
      }
    }
  }, [resource.current_meter_reading, resource.last_meter_reading !== null])

  const handleApplyFees = useCallback(
    async (invoice: IInvoice) => {
      const reservation = invoice.reservation
      if (!reservation) return

      // Retrieve the freshest values right when the callback runs
      const currentMeterReading = watch("current_meter_reading")
      const lastMeterReading = watch("last_meter_reading")
      const currentItems = watch("items")

      const postBody: ElectricPayload = !invoice.reservation?.site?.wild_energy_meter_location_id
        ? {
            site_id: reservation.site_id!,
            last_meter_reading: lastMeterReading ?? 0,
            current_meter_reading: currentMeterReading ?? 0,
          }
        : {
            site_id: reservation.site_id!,
            reservation_id: reservation.id!,
            is_syncing_readings: true,
          }

      try {
        const response = await Orders.createPreviewElectricPrices(postBody)

        const newElectricItems = response.order.preview.line_items

        if (response.last_meter_reading) setValue("last_meter_reading", Number(response.last_meter_reading))
        if (response.current_meter_reading) setValue("current_meter_reading", Number(response.current_meter_reading))

        const transformedNewElectricItems = newElectricItems.map((item) => ({
          ...item,
          unit_amount: Number(item.unit_amount_in_cents / 100.0),
          quantity: Number(item.quantity),
          total_amount: Number(item.total_amount_in_cents / 100.0),
        }))

        // Filter out any existing electric items from the current items
        const nonElectricItems = currentItems.filter((item: ILineItemField) => item.kind !== LINE_ITEM_KINDS.ELECTRIC)

        const updatedItems = [...nonElectricItems, ...transformedNewElectricItems] as Array<ILineItemField>

        setValue("items", updatedItems, { shouldValidate: true })
      } catch (error) {
        console.error("Error applying fees:", error)
      }
    },
    [watch, setValue],
  )

  return { handleApplyFees }
}

export default useApplyElectric
